import React, { MouseEvent } from 'react';
import cn from 'clsx';
import { ColorValueStrict } from 'fronton-react';
import { createElementKey, enhanceImageUrl, replaceSpecialChars, removeSpecialCharacters } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { lowerBoundsBreakpoints, strictBreakpoints } from 'common/styles/breakpoints';
import { Section } from '../section';
import {
  CategoryActionCard,
  CategoryImageGrid,
  CategoryImageGridMap,
  categoryItemImg,
  categoryImageTitle,
  invertCategoryCardGridMap,
} from './category-image-section-styles';
import { Link } from '../link';
import { Typography } from '../typography';
import { SectionSubTitle } from '../section-subtitle';

type Category = {
  title: string;
  href?: string;
  linkTitle?: string;
  imgAlt: string;
  srcD: string;
  srcT: string;
  src: string;
};

type CategoryImageSectionProps = {
  elementBlock: string;
  elementName: string;
  categories: Category[];
  invert?: boolean;
  textColor?: ColorValueStrict;
  title?: string;
  onClick?: (cardIndex: number) => void;
};

type ImageContentProps = {
  category: Category;
  textColor?: ColorValueStrict;
}

const ImageContent: React.FC<ImageContentProps> = ({ category, textColor }) => (
  <>
    <picture>
      <source
        srcSet={enhanceImageUrl(category.srcD)}
        media={lowerBoundsBreakpoints['--from-desktop']}
      />
      <source
        srcSet={enhanceImageUrl(category.srcT)}
        media={strictBreakpoints['--only-tablet']}
      />
      <img
        className={categoryItemImg}
        alt={category.imgAlt}
        src={category.src}
        loading="lazy"
      />
    </picture>
    <Typography variant="l" size="body_accent" color={textColor} className={categoryImageTitle}>
      {replaceSpecialChars(category.title)}
    </Typography>
  </>
)

export const CategoryImageSection: React.FC<CategoryImageSectionProps> = (
  { elementBlock, elementName, categories, invert, title, onClick, textColor }
) => {
  const handleCardImageClick = (cardTitle: string, cardIndex: number) => () => {
    if (onClick) {
      onClick(cardIndex);
    }

    analyticsUtils.clickButtonEvent(elementBlock, removeSpecialCharacters(cardTitle), elementName);
  };

  const handleCardImageWheelClick = (e: MouseEvent, cardTitle: string, cardIndex: number) => {
    if (e.button === 1) {
      handleCardImageClick(cardTitle, cardIndex)();
    }
  };

  return (
    <Section>
      {title && (
        <SectionSubTitle margin="m">{title}</SectionSubTitle>
      )}
      <CategoryImageGrid
        className={cn(
          CategoryImageGridMap[categories.length],
          { [invertCategoryCardGridMap[categories.length]]: invert }
        )}
      >
        {categories.map((category: Category, index) => (
          category.href ? (
            <Link
              href={category.href}
              onClick={handleCardImageClick(category.title, index)}
              onAuxClick={e => handleCardImageWheelClick(e, category.title, index)}
              key={createElementKey('categoryCard', index)}
              title={category.title}
            >
              <ImageContent category={category} textColor={textColor} />
            </Link>
          ) : (
            <CategoryActionCard
              key={createElementKey('categoryCard', index)}
              onClick={handleCardImageClick(category.title, index)}
            >
              <ImageContent category={category} textColor={textColor} />
            </CategoryActionCard>
          )
        ))}
      </CategoryImageGrid>
    </Section >
  );
};
